import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { Attachment, PostInput } from '../../lib/types'

/**
 * Лента — курсорная (api.posts → { items, nextCursor }). useInfiniteQuery тянет
 * страницы по cursor; `data` — плоский Post[] (страницы склеены селектором).
 * Ключ ['posts', 'infinite', channel] матчится префиксом ['posts'] в
 * invalidateQueries мутаций ниже (useReact/useCreatePost/…) — их трогать не надо.
 */
export function useInfinitePosts(channel?: string) {
  return useInfiniteQuery({
    queryKey: ['posts', 'infinite', channel],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => api.posts(channel, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    select: (data) => data.pages.flatMap((p) => p.items),
  })
}

export function usePost(id: string) {
  return useQuery({ queryKey: ['post', id], queryFn: () => api.post(id), enabled: !!id })
}

export function useReact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: 'LIKE' | 'DISLIKE' }) => api.react(id, type),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['posts'] })
      qc.invalidateQueries({ queryKey: ['post', id] })
    },
  })
}

export function useCreatePost(onDone: () => void) {
  const qc = useQueryClient()
  return useMutation({
    // attachmentIds — id из useUploadFile (POST /uploads) → картинки/файлы поста.
    mutationFn: (data: PostInput) => api.createPost(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts'] })
      onDone()
    },
  })
}

/** Правка поста — автор, admin или hr (модерация; иначе 403). */
export function useUpdatePost(id: string, onDone?: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<PostInput>) => api.updatePost(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts'] })
      qc.invalidateQueries({ queryKey: ['post', id] })
      onDone?.()
    },
  })
}

/** Удаление поста — автор, admin или hr (модерация); каскадно сносит комментарии/реакции. */
export function useDeletePost(onDone?: () => void) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.deletePost(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posts'] })
      onDone?.()
    },
  })
}

/**
 * Загрузка файла к посту: multipart → дескриптор Attachment, id которого
 * затем кладётся в PostInput.attachmentIds.
 * `file` — web `File` (из <input type=file>) или RN `{ uri, name, type }`.
 */
type UploadFile = File | { uri: string; name: string; type: string }
export function useUploadFile() {
  return useMutation<Attachment, Error, UploadFile>({
    mutationFn: (file: UploadFile) => {
      const form = new FormData()
      // Web принимает File напрямую; RN — объект { uri, name, type }.
      form.append('file', file as unknown as Blob)
      return api.uploadFile(form)
    },
  })
}
